import { useState, useEffect, useCallback } from 'react'
import { useAuthState } from '@/hooks/useAuth'
import { getUserVotesForWeek } from '@/lib/voteData'

// Global state to trigger updates across all components
let voteUpdateTrigger = 0
const voteUpdateListeners: (() => void)[] = []

// Function to trigger vote updates across all components
export const triggerVoteUpdate = () => {
  voteUpdateTrigger++
  voteUpdateListeners.forEach(listener => listener())
}

export function useUserVotes(weekIndex: number) {
  const { user } = useAuthState()
  const [userVotesThisWeek, setUserVotesThisWeek] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [updateTrigger, setUpdateTrigger] = useState(0)

  // Function to refresh votes
  const refreshVotes = useCallback(async () => {
    if (!user) {
      setUserVotesThisWeek([])
      return
    }

    setIsLoading(true)
    try {
      const votes = await getUserVotesForWeek(user.uid, weekIndex)
      const flashIds = votes.map(v => v.flashId)
      setUserVotesThisWeek(flashIds)
    } catch (error) {
      console.error("Failed to get user votes:", error)
      setUserVotesThisWeek([])
    } finally {
      setIsLoading(false)
    }
  }, [user, weekIndex])

  // Initial load and when dependencies change
  useEffect(() => {
    refreshVotes()
  }, [refreshVotes])

  // Listen for global vote updates
  useEffect(() => {
    const listener = () => {
      setUpdateTrigger(prev => prev + 1)
    }
    
    voteUpdateListeners.push(listener)
    
    return () => {
      const index = voteUpdateListeners.indexOf(listener)
      if (index > -1) {
        voteUpdateListeners.splice(index, 1)
      }
    }
  }, [])

  // Refresh votes when global update is triggered
  useEffect(() => {
    if (updateTrigger > 0) {
      refreshVotes()
    }
  }, [updateTrigger, refreshVotes])

  return {
    userVotesThisWeek,
    isLoading,
    refreshVotes,
    hasVotedForFlash: (flashId: string) => userVotesThisWeek.includes(flashId),
    remainingVotes: Math.max(0, 3 - userVotesThisWeek.length)
  }
} 